import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heading } from 'yoso';
import { DonBalance } from '~/components/Balance';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { BaseCard } from '~/components/Card';
import { myCitizenItemsUrl } from '~/components/Dosi/Dosi.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { Translation } from '~/components/I18n';
import { Citizen2D } from '~/components/Icon';
import { Dog2D } from '~/components/Icon/Dog2D';
import { Don2D } from '~/components/Icon/Don2D';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { useNftCount } from '~/components/Membership/Membership.hooks';
import { abbreviate, format } from '~/components/Util/number';
import styles from './AssetStatusCard.module.scss';

export type AssetStatusCardProps = {
  className?: string;
};

export const AssetStatusCard = ({ className }: AssetStatusCardProps) => {
  const { data: { amount: balance } = {} } = useBalance();
  const {
    data: { citizen, additional },
  } = useNftCount();
  const { countPerLevel = {} } = citizen || {};

  const citizenCount = useMemo(
    () =>
      Object.keys(countPerLevel).reduce((accumulator, key) => {
        const count = countPerLevel[key];
        return accumulator + count;
      }, 0),
    [countPerLevel]
  );

  const { open } = useEmbeddedViewer();
  const onItemClick = useCallback(() => {
    open(myCitizenItemsUrl);
  }, [open]);

  return (
    <BaseCard className={classNames(styles.root, className)} size="large" shadow>
      <Heading level={4} className={styles.heading}>
        <Translation name="citizen_home_holding_status_title" />
      </Heading>
      <DescriptionList className={styles.list}>
        <Link
          to="/membership/don/history"
          className={styles.listItem}
          data-event="click"
          data-event-target="dashboard_don_btn_in_myholdings">
          <dt>
            <Don2D className={styles.image} />
            DON
          </dt>
          <dd>{balance === 0 ? '-' : <DonBalance format={abbreviate} />}</dd>
        </Link>
        <div className={styles.divider} />
        <div
          className={styles.listItem}
          onClick={onItemClick}
          data-event="click"
          data-event-target="dashboard_citizen_btn_in_myholdings">
          <dt>
            <div className={classNames(styles.image, styles.citizenImageWrapper)}>
              <Citizen2D className={styles.citizenImage} />
            </div>
            Citizen
          </dt>
          <dd>{format(citizenCount, { zeroAsEmpty: true })}</dd>
        </div>
        <div className={styles.divider} />
        <div
          className={styles.listItem}
          onClick={onItemClick}
          data-event="click"
          data-event-target="dashboard_friends_btn_in_myholdings">
          <dt>
            <Dog2D className={styles.image} />
            Friends
          </dt>
          <dd>{format(additional?.friendsCount, { zeroAsEmpty: true })}</dd>
        </div>
      </DescriptionList>
    </BaseCard>
  );
};
